import type { Swiper as SwiperClass } from 'swiper/types';

import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useMemo } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useComparison } from '@screens/Comparison';
import { getSlidesQuantity } from '@screens/Comparison/utils/getSlidesQuantity/getSlidesQuantity';
import { ICON_DEFAULT } from '@shared/constants/colors';
import { useAppSelector } from '@shared/hooks/redux';
import { Container } from '@shared/ui/Container';
import { getScreenWidth } from '@shared/ui/ScreenSizeTracking';
import { getProductCardData } from '@shared/utils/getProductCardData/getProductCardData';
import { ProductCard } from '@widgets/ProductCard';

import cls from './CardsSlider.module.scss';

type CardsSliderProps = {
  mainSlider: SwiperClass | null | undefined;
  fixedSlider: SwiperClass | null | undefined;
  setMainSlider: (api: SwiperClass) => void;
};

export const CardsSlider = ({ mainSlider, setMainSlider, fixedSlider }: CardsSliderProps) => {
  const width = useAppSelector(getScreenWidth);

  const { products, activeCategory, controlSlider, changeSlide } = useComparison();

  const productsForCategory = useMemo(() => getProductCardData(products[activeCategory]), [activeCategory, products]);
  const minQuantitySlides = Object.values(products[activeCategory])?.length > getSlidesQuantity();

  const nextSlideHandler = () => {
    fixedSlider?.slideNext();
    mainSlider?.slideNext();
  };

  const prevSlideHandler = () => {
    fixedSlider?.slidePrev();
    mainSlider?.slidePrev();
  };

  return (
    <div className={cls.wrap}>
      <Container className={cls.container}>
        {controlSlider.prev && (
          <button
            onClick={prevSlideHandler}
            className={cls.prev}
            aria-label={'Предыдущий слайд с товарами сравнения'}
          >
            <ArrowLeft
              color={ICON_DEFAULT}
              size={16}
            />
          </button>
        )}

        <Swiper
          className={cls.slider}
          spaceBetween={12}
          slidesPerView={2}
          breakpoints={{
            768: {
              slidesPerView: 3
            },
            1081: {
              slidesPerView: 4
            }
          }}
          allowTouchMove={false}
          onResize={(api) => {
            if (width < 1281) {
              api.allowTouchMove = true;
            }
          }}
          onSwiper={setMainSlider}
          onSlideChange={(api) => {
            changeSlide(api.realIndex, minQuantitySlides && !api.isBeginning, minQuantitySlides && !api.isEnd);
          }}
        >
          {productsForCategory.map((el) => (
            <SwiperSlide
              className={cls.slide}
              key={el.id}
            >
              <ProductCard
                view={'grid'}
                data={el}
                comparisonPage={true}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        {controlSlider.next && (
          <button
            onClick={nextSlideHandler}
            className={cls.next}
            aria-label={'Следующий слайд с товарами сравнения'}
          >
            <ArrowRight
              size={16}
              color={ICON_DEFAULT}
            />
          </button>
        )}
      </Container>
    </div>
  );
};
